import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Dialog, EmptyState, LoadingState } from "@note-app/ui";
import type { KnowledgeItem } from "@note-app/contracts";

import type { KnowledgeRoute } from "../routes.ts";
import type { KnowledgeAppViewModel } from "../view-model.ts";
import { KnowledgeIcon, type KnowledgeIconName } from "./KnowledgeIcon.tsx";

type SearchScope = "all" | "document" | "canvas" | "command";

interface SearchCommand {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly icon: KnowledgeIconName;
  readonly keywords: readonly string[];
  readonly run: () => void | Promise<void>;
}

type SearchState =
  | { readonly status: "loading" }
  | { readonly status: "ready"; readonly items: readonly KnowledgeItem[] }
  | { readonly status: "error"; readonly message: string };

export interface GlobalSearchDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly viewModel: KnowledgeAppViewModel;
  readonly navigate: (route: KnowledgeRoute) => void;
  readonly onQuickCapture: () => void;
  readonly onCreateDocument: () => void | Promise<void>;
  readonly onCreateCanvas: () => void | Promise<void>;
}

const scopes: ReadonlyArray<{ id: SearchScope; label: string }> = [
  { id: "all", label: "全部" },
  { id: "document", label: "文档" },
  { id: "canvas", label: "画板" },
  { id: "command", label: "命令" },
];

export function GlobalSearchDialog({
  open,
  onOpenChange,
  viewModel,
  navigate,
  onQuickCapture,
  onCreateDocument,
  onCreateCanvas,
}: GlobalSearchDialogProps) {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<SearchScope>("all");
  const [state, setState] = useState<SearchState>({ status: "loading" });
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchGeneration = useRef(0);
  const resultsId = useId();

  const closeAndRun = useCallback(
    (work: () => void | Promise<void>) => {
      onOpenChange(false);
      void work();
    },
    [onOpenChange],
  );

  const commands = useMemo<readonly SearchCommand[]>(
    () => [
      {
        id: "new-document",
        label: "新建文档",
        description: "创建一篇空白块文档",
        icon: "document",
        keywords: ["new", "note", "文档", "笔记"],
        run: () => closeAndRun(onCreateDocument),
      },
      {
        id: "new-canvas",
        label: "新建画板",
        description: "创建自由画板进行视觉思考",
        icon: "canvas",
        keywords: ["new", "board", "whiteboard", "画板"],
        run: () => closeAndRun(onCreateCanvas),
      },
      {
        id: "quick-capture",
        label: "快速记录",
        description: "不离开当前页面，先收下此刻想法",
        icon: "inbox",
        keywords: ["capture", "inbox", "记录", "收件箱"],
        run: () => closeAndRun(onQuickCapture),
      },
      {
        id: "open-settings",
        label: "设置",
        description: "内容管理、回收站、备份和恢复",
        icon: "shield",
        keywords: ["settings", "backup", "安全", "设置"],
        run: () => closeAndRun(() => navigate({ name: "settings" })),
      },
    ],
    [closeAndRun, navigate, onCreateCanvas, onCreateDocument, onQuickCapture],
  );

  useEffect(() => {
    if (!open) {
      searchGeneration.current += 1;
      setQuery("");
      setScope("all");
      setActiveIndex(0);
      return;
    }
    const generation = ++searchGeneration.current;
    setState({ status: "loading" });
    const timer = window.setTimeout(() => {
      void viewModel.search(query).then(
        (items) => {
          if (generation === searchGeneration.current) {
            setState({ status: "ready", items });
          }
        },
        (cause: unknown) => {
          if (generation === searchGeneration.current) {
            setState({
              status: "error",
              message:
                cause instanceof Error ? cause.message : "搜索本机内容失败。",
            });
          }
        },
      );
    }, query ? 140 : 0);
    return () => window.clearTimeout(timer);
  }, [open, query, viewModel]);

  const normalizedQuery = query.trim().toLocaleLowerCase();
  const visibleItems =
    state.status === "ready"
      ? state.items
          .filter((item) => scope === "all" || item.kind === scope)
          .slice(0, 8)
      : [];
  const visibleCommands =
    scope === "all" || scope === "command"
      ? commands.filter((command) => {
          if (!normalizedQuery) return true;
          return [command.label, command.description, ...command.keywords]
            .join(" ")
            .toLocaleLowerCase()
            .includes(normalizedQuery);
        })
      : [];
  const choices: ReadonlyArray<
    | { readonly kind: "item"; readonly value: KnowledgeItem }
    | { readonly kind: "command"; readonly value: SearchCommand }
  > = [
    ...visibleItems.map((value) => ({ kind: "item" as const, value })),
    ...visibleCommands.map((value) => ({ kind: "command" as const, value })),
  ];
  const safeActiveIndex = choices.length
    ? Math.min(activeIndex, choices.length - 1)
    : 0;

  const activate = (index: number) => {
    const choice = choices[index];
    if (!choice) return;
    if (choice.kind === "command") {
      void choice.value.run();
      return;
    }
    const item = choice.value;
    onOpenChange(false);
    navigate(
      item.kind === "document"
        ? { name: "document", itemId: item.id }
        : { name: "canvas", itemId: item.id },
    );
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) =>
        choices.length ? (index + 1) % choices.length : 0,
      );
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) =>
        choices.length ? (index - 1 + choices.length) % choices.length : 0,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      activate(safeActiveIndex);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="全局搜索"
      description="搜索笔记、画板，或直接执行常用命令。"
      closeLabel="关闭全局搜索"
      initialFocusRef={inputRef}
      className="knowledge-global-search"
    >
      <label className="knowledge-global-search__input">
        <KnowledgeIcon name="search" />
        <span className="ui-visually-hidden">搜索笔记和命令</span>
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          aria-label="搜索笔记和命令"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={resultsId}
          aria-activedescendant={
            choices.length ? `${resultsId}-option-${safeActiveIndex}` : undefined
          }
          value={query}
          placeholder="搜索笔记、画板或命令"
          autoComplete="off"
          onChange={(event) => {
            setQuery(event.currentTarget.value);
            setActiveIndex(0);
          }}
          onKeyDown={handleKeyDown}
        />
        <kbd>Esc</kbd>
      </label>

      <div className="knowledge-global-search__scopes" aria-label="搜索范围">
        {scopes.map((candidate) => (
          <button
            key={candidate.id}
            type="button"
            aria-pressed={scope === candidate.id}
            onClick={() => {
              setScope(candidate.id);
              setActiveIndex(0);
            }}
          >
            {candidate.label}
          </button>
        ))}
      </div>

      <div
        id={resultsId}
        className="knowledge-global-search__results"
        role="listbox"
        aria-label="搜索结果"
      >
        {state.status === "loading" ? (
          <LoadingState compact label="正在搜索本机知识库" lines={4} />
        ) : state.status === "error" ? (
          <EmptyState
            compact
            title="暂时无法搜索"
            description={state.message}
            icon={<KnowledgeIcon name="search" />}
          />
        ) : choices.length === 0 ? (
          <EmptyState
            compact
            title="没有找到匹配内容"
            description="可以尝试更短的关键词，或切换搜索范围。"
            icon={<KnowledgeIcon name="search" />}
          />
        ) : (
          choices.map((choice, index) => {
            const title =
              choice.kind === "item" ? choice.value.title : choice.value.label;
            const description =
              choice.kind === "item"
                ? `${choice.value.kind === "document" ? "文档" : "画板"} · ${formatRelativeTime(choice.value.updatedAt)}`
                : choice.value.description;
            const icon: KnowledgeIconName =
              choice.kind === "item"
                ? choice.value.kind === "document"
                  ? "document"
                  : "canvas"
                : choice.value.icon;
            return (
              <button
                key={`${choice.kind}-${choice.value.id}`}
                id={`${resultsId}-option-${index}`}
                type="button"
                role="option"
                aria-selected={index === safeActiveIndex}
                onMouseMove={() => setActiveIndex(index)}
                onClick={() => activate(index)}
              >
                <span className="knowledge-global-search__result-icon" aria-hidden="true">
                  <KnowledgeIcon name={icon} />
                </span>
                <span>
                  <strong>{title}</strong>
                  <small>{description}</small>
                </span>
                <span className="knowledge-global-search__result-kind">
                  {choice.kind === "command" ? "命令" : "打开"}
                </span>
              </button>
            );
          })
        )}
      </div>

      <footer className="knowledge-global-search__footer">
        <span><kbd>↑</kbd><kbd>↓</kbd> 选择</span>
        <span><kbd>Enter</kbd> 打开</span>
        <button
          type="button"
          onClick={() => {
            onOpenChange(false);
            navigate({ name: "search", query });
          }}
        >
          打开完整搜索
        </button>
      </footer>
    </Dialog>
  );
}

function formatRelativeTime(timestamp: number): string {
  const delta = Date.now() - timestamp;
  const minutes = Math.max(1, Math.round(delta / 60_000));
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  return new Intl.DateTimeFormat("zh-CN", {
    month: "short",
    day: "numeric",
  }).format(new Date(timestamp));
}
